import { logger } from '../utils/logger'
import * as SunCalc from 'suncalc'

/**
 * Smart date/time calculator with API integrations
 */
export class SmartTimingService {
  private weatherApiKey: string
  private geocodingApiKey: string

  constructor(
    weatherApiKey: string = process.env.OPENWEATHERMAP_API_KEY || '',
    geocodingApiKey: string = process.env.MAPBOX_TOKEN || ''
  ) {
    this.weatherApiKey = weatherApiKey
    this.geocodingApiKey = geocodingApiKey
  }

  /**
   * Calculate optimal timing for a segment based on:
   * - Weather forecast
   * - Sunrise/sunset times
   * - Time of day preference
   * - Traffic patterns
   */
  async calculateOptimalTiming(segment: any, location: any): Promise<{
    optimalTime: Date
    weatherScore: number
    lightingScore: number
    crowdingScore: number
    overallScore: number
    reasoning: string
  }> {
    const { timeOfDayPreference, weatherSensitive, absoluteTime } = segment

    // If user specified exact time, use it
    if (absoluteTime) {
      const specifiedTime = new Date(absoluteTime)
      return {
        optimalTime: specifiedTime,
        weatherScore: 100,
        lightingScore: 100,
        crowdingScore: 100,
        overallScore: 100,
        reasoning: 'Heure spécifiée par l\'utilisateur'
      }
    }

    // Get sunrise/sunset times
    const sunTimes = await this.getSunTimes(location, new Date())

    // Get weather forecast
    let weatherScore = 100
    if (weatherSensitive && this.weatherApiKey) {
      const weather = await this.getWeatherForecast(location)
      weatherScore = this.scoreWeather(weather)
    }

    // Calculate optimal time based on preference
    let optimalTime: Date
    let lightingScore = 100
    let crowdingScore = 100
    let reasoning = ''

    switch (timeOfDayPreference) {
      case 'sunrise':
        optimalTime = new Date(sunTimes.sunrise.getTime() - 15 * 60000) // 15 min before
        lightingScore = 100
        reasoning = 'Optimisé pour le lever du soleil'
        break

      case 'sunset':
        optimalTime = new Date(sunTimes.sunset.getTime() - 30 * 60000) // 30 min before
        lightingScore = 100
        reasoning = 'Optimisé pour le coucher du soleil'
        break

      case 'morning':
        optimalTime = new Date()
        optimalTime.setHours(9, 0, 0, 0) // 9 AM
        crowdingScore = 85 // Moderate crowds
        reasoning = 'Matinée, foule modérée'
        break

      case 'afternoon':
        optimalTime = new Date()
        optimalTime.setHours(14, 30, 0, 0) // 2:30 PM
        crowdingScore = 70 // Higher crowds
        reasoning = 'Après-midi, plus de monde'
        break

      case 'evening':
        optimalTime = new Date()
        optimalTime.setHours(19, 0, 0, 0) // 7 PM
        crowdingScore = 90 // Lower crowds
        reasoning = 'Soirée, moins de monde'
        break

      case 'night':
        optimalTime = new Date()
        optimalTime.setHours(21, 0, 0, 0) // 9 PM
        lightingScore = 60 // Low natural light
        crowdingScore = 95 // Very few crowds
        reasoning = 'Nuit, très peu de monde'
        break

      default:
        // No preference - choose based on activity type
        optimalTime = this.guessOptimalTimeByActivityType(segment, sunTimes)
        reasoning = 'Horaire suggéré selon l\'activité'
        crowdingScore = 80
    }

    const overallScore = (weatherScore * 0.4 + lightingScore * 0.3 + crowdingScore * 0.3)

    return {
      optimalTime,
      weatherScore,
      lightingScore,
      crowdingScore,
      overallScore,
      reasoning
    }
  }

  /**
   * Get sunrise/sunset times for a location using SunCalc
   */
  private async getSunTimes(location: any, date: Date): Promise<{
    sunrise: Date
    sunset: Date
    goldenHourStart: Date
    goldenHourEnd: Date
  }> {
    const { lat, lng } = location

    // Use SunCalc for precise calculations based on lat/lng/date
    const times = SunCalc.getTimes(date, lat, lng)

    const sunrise = times.sunrise
    const sunset = times.sunset
    const goldenHourStart = times.goldenHour // Evening golden hour start
    const goldenHourEnd = sunset

    logger.debug('Sun times calculated', {
      lat, lng, date: date.toISOString(),
      sunrise: sunrise.toISOString(),
      sunset: sunset.toISOString(),
      goldenHour: goldenHourStart.toISOString()
    })

    return { sunrise, sunset, goldenHourStart, goldenHourEnd }
  }

  /**
   * Get weather forecast from OpenWeatherMap
   */
  private async getWeatherForecast(location: any): Promise<any> {
    if (!this.weatherApiKey) {
      logger.warn('Weather API key not configured')
      return null
    }

    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?lat=${location.lat}&lon=${location.lng}&appid=${this.weatherApiKey}&units=metric`
      )

      if (!response.ok) {
        throw new Error(`Weather API error: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      logger.error('Failed to fetch weather:', error)
      return null
    }
  }

  /**
   * Score weather conditions (0-100)
   */
  private scoreWeather(weather: any): number {
    if (!weather || !weather.list || weather.list.length === 0) {
      return 100 // No data = assume good
    }

    const forecast = weather.list[0] // Next 3h
    const { main, weather: conditions } = forecast

    let score = 100

    // Temperature penalty (too hot or too cold)
    const temp = main.temp
    if (temp < 5 || temp > 35) score -= 30
    else if (temp < 10 || temp > 30) score -= 15

    // Rain/snow penalty
    const weatherId = conditions[0]?.id || 800
    if (weatherId >= 200 && weatherId < 300) score -= 50 // Thunderstorm
    if (weatherId >= 300 && weatherId < 500) score -= 20 // Drizzle
    if (weatherId >= 500 && weatherId < 600) score -= 40 // Rain
    if (weatherId >= 600 && weatherId < 700) score -= 30 // Snow

    // Wind penalty
    const windSpeed = forecast.wind?.speed || 0
    if (windSpeed > 10) score -= 20
    else if (windSpeed > 7) score -= 10

    return Math.max(0, score)
  }

  /**
   * Guess optimal time based on activity type
   */
  private guessOptimalTimeByActivityType(segment: any, sunTimes: any): Date {
    const { categories = [], tags = [] } = segment
    const now = new Date()

    // Outdoor activities → golden hour
    if (tags.includes('outdoor') || tags.includes('scenic') || categories.includes('nature')) {
      return sunTimes.goldenHourStart
    }

    // Restaurants → lunch (12:30) or dinner (19:30)
    if (categories.includes('restaurant')) {
      const hour = now.getHours()
      if (hour < 14) {
        const lunchTime = new Date(now)
        lunchTime.setHours(12, 30, 0, 0)
        return lunchTime
      } else {
        const dinnerTime = new Date(now)
        dinnerTime.setHours(19, 30, 0, 0)
        return dinnerTime
      }
    }

    // Shopping/groceries → morning (avoid crowds)
    if (categories.includes('grocery') || categories.includes('shopping')) {
      const morningTime = new Date(now)
      morningTime.setHours(9, 0, 0, 0)
      return morningTime
    }

    // Culture/museums → afternoon (open hours)
    if (categories.includes('culture') || categories.includes('historic')) {
      const afternoonTime = new Date(now)
      afternoonTime.setHours(14, 0, 0, 0)
      return afternoonTime
    }

    // Default: 1 hour from now
    return new Date(now.getTime() + 60 * 60000)
  }

  /**
   * Calculate relevance score for a POI based on:
   * - Distance from route
   * - User preferences
   * - Weather suitability
   * - Time of day suitability
   * - Crowding prediction
   */
  async calculateRelevanceScore(poi: any, segment: any, context: any): Promise<{
    score: number
    breakdown: {
      distance: number
      preferences: number
      weather: number
      timing: number
      crowding: number
      popularity: number
    }
    reasoning: string
  }> {
    const breakdown = {
      distance: 0,
      preferences: 0,
      weather: 0,
      timing: 0,
      crowding: 0,
      popularity: 0
    }

    // 1. Distance score (closer = better)
    const detourKm = context.detourKm || 0
    if (detourKm < 1) breakdown.distance = 100
    else if (detourKm < 3) breakdown.distance = 90
    else if (detourKm < 5) breakdown.distance = 75
    else if (detourKm < 10) breakdown.distance = 50
    else breakdown.distance = 25

    // 2. Preferences score (category match)
    const categoryMatch = segment.categories?.some((cat: string) =>
      poi.categories?.includes(cat)
    )
    breakdown.preferences = categoryMatch ? 100 : 50

    // 3. Weather score
    if (segment.weatherSensitive) {
      const weather = await this.getWeatherForecast(poi.location)
      breakdown.weather = this.scoreWeather(weather)
    } else {
      breakdown.weather = 100
    }

    // 4. Timing score
    const timing = await this.calculateOptimalTiming(segment, poi.location)
    breakdown.timing = timing.lightingScore

    // 5. Crowding score (predict based on time/day)
    breakdown.crowding = this.predictCrowding(poi, new Date())

    // 6. Popularity score (confidence from DB)
    breakdown.popularity = poi.confidence || 80

    // Weighted average
    const score = (
      breakdown.distance * 0.25 +
      breakdown.preferences * 0.20 +
      breakdown.weather * 0.15 +
      breakdown.timing * 0.15 +
      breakdown.crowding * 0.10 +
      breakdown.popularity * 0.15
    )

    const reasoning = `Distance: ${detourKm.toFixed(1)}km, Catégorie: ${categoryMatch ? 'Match' : 'Partiel'}, Météo: ${breakdown.weather}/100`

    return { score, breakdown, reasoning }
  }

  /**
   * Predict crowding level (0-100, higher = less crowded)
   */
  private predictCrowding(poi: any, time: Date): number {
    const hour = time.getHours()
    const day = time.getDay() // 0 = Sunday

    let score = 80 // Default moderate

    // Weekend = more crowded
    if (day === 0 || day === 6) score -= 15

    // Peak hours = more crowded
    if (hour >= 12 && hour <= 14) score -= 20 // Lunch
    if (hour >= 19 && hour <= 21) score -= 15 // Dinner

    // Early morning/late evening = less crowded
    if (hour < 9 || hour > 21) score += 15

    return Math.max(0, Math.min(100, score))
  }

  /**
   * Calculate route distance using Mapbox Directions API
   * Falls back to Haversine if API unavailable
   */
  async calculateRouteDistance(
    from: { lat: number; lng: number },
    to: { lat: number; lng: number }
  ): Promise<{ distanceKm: number; durationMin: number; source: 'mapbox' | 'haversine' }> {
    const mapboxToken = this.geocodingApiKey

    if (mapboxToken) {
      try {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${from.lng},${from.lat};${to.lng},${to.lat}?access_token=${mapboxToken}&geometries=geojson`
        const response = await fetch(url)

        if (response.ok) {
          const data = await response.json() as { routes?: any[] }

          if (data.routes && data.routes.length > 0) {
            const route = data.routes[0]
            const distanceKm = route.distance / 1000
            const durationMin = Math.round(route.duration / 60)

            logger.debug('Mapbox route calculated', { distanceKm, durationMin })

            return { distanceKm, durationMin, source: 'mapbox' }
          }
        }
      } catch (error) {
        logger.warn('Mapbox Directions API failed, falling back to Haversine', { error })
      }
    }

    // Fallback to Haversine formula
    const R = 6371 // Earth radius in km
    const dLat = (to.lat - from.lat) * Math.PI / 180
    const dLng = (to.lng - from.lng) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(from.lat * Math.PI / 180) * Math.cos(to.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distanceKm = R * c

    // Estimate duration at 50 km/h average
    const durationMin = Math.round((distanceKm / 50) * 60)

    return { distanceKm, durationMin, source: 'haversine' }
  }
}
